import React, { createContext, useCallback, useContext } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import useResilientSubscription from '@/hooks/useResilientSubscription';
import { EXPERIENCE_DATA, ADULT_RIG_DATA } from '@/components/booking/bookingDefaults';

const BookingConfigContext = createContext(null);

// Merge DB-managed records over the hardcoded defaults, keyed by `key`.
// Returns the same shape the storefront already expects, so components
// keep working even before any admin record exists.
function mergeWithDefaults(records, defaults) {
  const map = {};
  for (const [key, val] of Object.entries(defaults)) {
    map[key] = { ...val, key, active: true };
  }
  // Drop undefined fields so a DB record without (e.g.) description doesn't
  // clobber the default value from bookingDefaults.
  const defined = (rec) => {
    const out = {};
    for (const [k, v] of Object.entries(rec)) if (v !== undefined) out[k] = v;
    return out;
  };
  for (const rec of records) {
    if (rec.key && map[rec.key]) {
      map[rec.key] = { ...map[rec.key], ...defined(rec) };
    } else if (rec.key) {
      // Admin-created custom records (not in defaults) still appear on the storefront.
      map[rec.key] = { ...defined(rec) };
    }
  }
  return map;
}

export function BookingConfigProvider({ children }) {
  const queryClient = useQueryClient();

  const { data: expRecords = [], isLoading: expLoading } = useQuery({
    queryKey: ['booking-experiences'],
    queryFn: () => base44.entities.BookingExperience.list('sort_order'),
  });

  const { data: rigRecords = [], isLoading: rigLoading } = useQuery({
    queryKey: ['booking-rigs'],
    queryFn: () => base44.entities.BookingRig.list('sort_order'),
  });

  // Experiences and bays are edited in the admin area and change state on the
  // floor (maintenance, dispatch), so the till and the booking flow refresh in
  // step with those changes instead of needing a reload. The slow poll is a
  // backstop for a connection that has silently dropped.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['booking-experiences'] });
    queryClient.invalidateQueries({ queryKey: ['booking-rigs'] });
  }, [queryClient]);

  useResilientSubscription({
    entities: ['BookingExperience', 'BookingRig'],
    onEvent: invalidate,
    onResync: invalidate,
    pollMs: 60000,
  });

  const experiences = mergeWithDefaults(expRecords, EXPERIENCE_DATA);

  // A bay under maintenance is withheld from every picker this context powers
  // (the booking flow and the walk-in POS), so it can never be booked while it
  // is off the floor. Filtering happens after the merge, because the hardcoded
  // defaults would otherwise put the rig straight back.
  const rigs = Object.fromEntries(
    Object.entries(mergeWithDefaults(rigRecords, ADULT_RIG_DATA)).filter(
      ([, rig]) => !rig.maintenance
    )
  );

  return (
    <BookingConfigContext.Provider value={{ experiences, rigs, loading: expLoading || rigLoading }}>
      {children}
    </BookingConfigContext.Provider>
  );
}

export function useBookingConfig() {
  const ctx = useContext(BookingConfigContext);
  if (!ctx) {
    return { experiences: EXPERIENCE_DATA, rigs: ADULT_RIG_DATA, loading: false };
  }
  return ctx;
}